import { AGENT_PAGES } from './catalog';
import type { AgentArgs } from './registry';

// ═══════════════════════════════════════════════
// / 命令模板: 不经过大模型, 确定性地翻译成命令步骤。
// 没配 AI、或者想要可复现的操作时用它; 也是给模型的"标准动作"示范。
// ═══════════════════════════════════════════════

export type PlannedStep = { command: string; args: AgentArgs; reason?: string };

export type TemplateResult =
  | { ok: true; steps: PlannedStep[] }
  | { ok: false; error: string; vars?: Record<string, string | number> };

export const AGENT_TEMPLATES: Array<{ usage: string; description: string }> = [
  { usage: '/跳转 压裂模拟', description: '打开页面 (页面名见侧边栏)' },
  { usage: '/状态', description: '读取当前页面状态' },
  { usage: '/方案列表', description: '列出项目的压裂方案' },
  { usage: '/生成方案 [补充要求]', description: '打开 AI 生成方案对话框' },
  { usage: '/激活方案 12', description: '设为当前方案' },
  { usage: '/智能分段', description: '打开智能分段对话框' },
  { usage: '/保存分段', description: '保存分段分簇方案' },
  { usage: '/完井设计', description: '完井与射孔智能设计' },
  { usage: '/保存完井', description: '保存完井与射孔方案' },
  { usage: '/保存材料', description: '保存材料设计' },
  { usage: '/生成泵注', description: '智能生成泵注程序' },
  { usage: '/保存泵注', description: '保存泵注程序' },
  { usage: '/模拟 [段 1,2,3]', description: '用当前方案运行模拟, 不写段号=全部段' },
  { usage: '/模拟 方案 12', description: '切换方案并自动计算全部段' },
];

const SIMPLE: Record<string, string> = {
  '/状态': 'page.status',
  '/方案列表': 'design.list',
  '/智能分段': 'staging.smart_design',
  '/保存分段': 'staging.save',
  '/完井设计': 'completion.smart_design',
  '/保存完井': 'completion.save',
  '/保存材料': 'materials.save',
  '/生成泵注': 'pump.generate',
  '/保存泵注': 'pump.save',
};

// 英文界面下的同义命令, 解析时先换成中文命令
const ENGLISH_ALIASES: Record<string, string> = {
  '/goto': '/跳转', '/status': '/状态', '/designs': '/方案列表', '/new-design': '/生成方案',
  '/activate': '/激活方案', '/staging': '/智能分段', '/save-staging': '/保存分段',
  '/completion': '/完井设计', '/save-completion': '/保存完井', '/save-materials': '/保存材料',
  '/pump': '/生成泵注', '/save-pump': '/保存泵注', '/simulate': '/模拟',
};

const parseNumberList = (text: string): number[] =>
  text.split(/[,，、\s]+/).map((v) => Number(v)).filter((n) => Number.isInteger(n) && n > 0);

export const isTemplateInput = (text: string) => text.trim().startsWith('/');

export function parseTemplate(input: string): TemplateResult {
  const text = input.trim().replace(/\s+/g, ' ');
  const [rawHead, ...rest] = text.split(' ');
  const head = ENGLISH_ALIASES[rawHead.toLowerCase()] ?? rawHead;
  const tail = rest.join(' ').trim();

  if (head === '/帮助' || head === '/help') return { ok: false, error: 'help' };

  if (SIMPLE[head]) {
    return tail
      ? { ok: false, error: '{cmd} 不需要参数', vars: { cmd: head } }
      : { ok: true, steps: [{ command: SIMPLE[head], args: {} }] };
  }

  if (head === '/跳转') {
    const page = AGENT_PAGES.find((p) => p.label === tail || p.value === tail)
      ?? AGENT_PAGES.find((p) => tail && p.label.includes(tail));
    return page
      ? { ok: true, steps: [{ command: 'nav.goto', args: { page: page.value } }] }
      : { ok: false, error: '找不到页面「{page}」', vars: { page: tail } };
  }

  if (head === '/生成方案') {
    return { ok: true, steps: [{ command: 'design.ai_generate', args: tail ? { brief: tail } : {} }] };
  }

  if (head === '/激活方案') {
    const id = Number(tail);
    return Number.isInteger(id) && id > 0
      ? { ok: true, steps: [{ command: 'design.activate', args: { design_id: id } }] }
      : { ok: false, error: '请写方案编号, 例如 /激活方案 12' };
  }

  if (head === '/模拟') {
    const steps: PlannedStep[] = [];
    const designMatch = tail.match(/(?:方案|design)\s*(\d+)/i);
    const stageMatch = tail.match(/(?:段|stages?)\s*([\d,，、\s]+)/i);
    const leftover = tail.replace(/(?:方案|design)\s*\d+/i, '').replace(/(?:段|stages?)\s*[\d,，、\s]+/i, '').replace(/全部|all/i, '').trim();
    if (leftover) return { ok: false, error: '无法识别「{text}」, 格式: /模拟 方案 12 段 1,2,3', vars: { text: leftover } };
    if (designMatch && stageMatch) {
      return { ok: false, error: '切换方案会自动计算全部段: 请先 /模拟 方案 {id}, 再用 /模拟 段 … 计算指定段', vars: { id: designMatch[1] } };
    }
    if (designMatch) return { ok: true, steps: [{ command: 'fracsim.select_design', args: { design_id: Number(designMatch[1]) } }] };
    const stages = stageMatch ? parseNumberList(stageMatch[1]) : [];
    if (stageMatch && !stages.length) return { ok: false, error: '段号无效, 例如 段 1,2,3' };
    steps.push({ command: 'fracsim.select_stages', args: stages.length ? { stages } : {} });
    steps.push({ command: 'fracsim.run', args: {} });
    return { ok: true, steps };
  }

  return { ok: false, error: '未知命令 {cmd}, 输入 /帮助 查看可用命令', vars: { cmd: head } };
}
